import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Button,
    Switch,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    IconButton,
    Avatar,
    Chip,
    Tooltip,
    MenuItem
} from '@mui/material';
import { Edit, Delete, Add, PhotoCamera, Refresh } from '@mui/icons-material';
import { uploadImagecertifates } from '../../Redux/Actions/Action';
import { validateFile } from '../../utils/comman-function';

const TYPE_OPTIONS = [
    { value: 'my_sessions', label: 'My Sessions' },
    { value: 'first_free_session', label: 'First Free Session' },
    { value: 'wallet_transactions', label: 'Wallet Transactions' },
    { value: 'book_a_pooja', label: 'Book a Pooja' },
    { value: 'book_a_spell', label: 'Book a Spell' },
    { value: 'my_appointments', label: 'My Appointments' },
    { value: 'following_astrologers', label: 'Following Astrologers' },
    { value: 'pooja_wishlist', label: 'Pooja Wishlist' },
    { value: 'available_coupons', label: 'Available Coupons' },
    { value: 'refer_a_friend', label: 'Refer a Friend' },
    { value: 'help_support', label: 'Help & Support' },
    { value: 'about_us', label: 'About Us' },
    { value: 'faq', label: 'FAQ' },
    { value: 'terms_conditions', label: 'Terms & Conditions' },
    { value: 'privacy_policy', label: 'Privacy Policy' },
    { value: 'share_app', label: 'Share App' },
    { value: 'home', label: 'Home' },
    { value: 'custom_url', label: 'Custom URL' }
];

const HamburgerMenu = () => {
    const dispatch = useDispatch();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalDocs, setTotalDocs] = useState(0);

    // Dialog state
    const [openModal, setOpenModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        iconUrl: '',
        badgeText: '',
        isActive: true
    });
    const [imageUploading, setImageUploading] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState({});
    const [guestStatusUpdating, setGuestStatusUpdating] = useState({});

    // Delete confirmation state
    const [deleteId, setDeleteId] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const token = localStorage.getItem('astroVani-admin-token');
    const baseUrl = process.env.REACT_APP_BASEURL;

    const fetchItems = useCallback(async (targetPage = page, targetLimit = pageSize) => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${baseUrl}/admin/nav-icons/hamburger-menu?page=${targetPage}&limit=${targetLimit}`,
                {
                    headers: { Authorization: token }
                }
            );

            let dataList = [];
            if (Array.isArray(res?.data?.data)) {
                dataList = res.data.data;
            } else if (Array.isArray(res?.data)) {
                dataList = res.data;
            }

            setItems(dataList);

            // Handle pagination details from API
            const pagination = res?.data?.paginationDetail;
            if (pagination) {
                setTotalDocs(pagination.totalDocs || dataList.length);
            } else {
                setTotalDocs(res?.data?.totalDocs ?? dataList.length);
            }
        } catch (error) {
            console.error('Error fetching hamburger menu items:', error);
            toast.error(error?.response?.data?.message || 'Failed to fetch Hamburger Menu items');
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, page, pageSize]);

    useEffect(() => {
        fetchItems(page, pageSize);
    }, [fetchItems, page, pageSize]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditId(item._id);
            setFormData({
                name: item.name || '',
                type: item.type || '',
                iconUrl: item.iconUrl || '',
                badgeText: item.badgeText || '',
                isActive: item.isActive ?? true
            });
        } else {
            setEditId(null);
            setFormData({
                name: '',
                type: '',
                iconUrl: '',
                badgeText: '',
                isActive: true
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditId(null);
        setFormData({ name: '', type: '', iconUrl: '', badgeText: '', isActive: true });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!validateFile(file, 'image')) {
            event.target.value = '';
            return;
        }

        setImageUploading(true);
        try {
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            if (uploadedUrl) {
                setFormData(prev => ({ ...prev, iconUrl: uploadedUrl.toString() }));
                toast.success('Image uploaded successfully!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Error uploading image.');
        } finally {
            setImageUploading(false);
            event.target.value = '';
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Please enter a name.');
            return;
        }
        if (!editId && !formData.type) {
            toast.error('Please select a type.');
            return;
        }
        if (!formData.iconUrl.trim()) {
            toast.error('Please upload an icon image.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formData.name.trim(),
                iconUrl: formData.iconUrl.trim(),
                badgeText: formData.badgeText.trim() ? formData.badgeText.trim() : null
            };

            if (formData.type) {
                payload.type = formData.type;
            }

            if (!editId) {
                payload.isActive = true;
                const fallbackOrder = items.length > 0 ? Math.max(...items.map(i => Number(i.order) || 0)) + 1 : 1;
                payload.order = fallbackOrder;
                await axios.post(`${baseUrl}/admin/nav-icons/hamburger-menu`, payload, {
                    headers: { Authorization: token, 'Content-Type': 'application/json' }
                });
                toast.success('Hamburger Menu item created successfully');
            } else {
                await axios.patch(`${baseUrl}/admin/nav-icons/hamburger-menu/${editId}`, payload, {
                    headers: { Authorization: token, 'Content-Type': 'application/json' }
                });
                toast.success('Hamburger Menu item updated successfully');
            }

            handleCloseModal();
            fetchItems(page, pageSize);
        } catch (error) {
            console.error('Save error:', error);
            toast.error(error?.response?.data?.message || 'Failed to save Hamburger Menu item');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = (id) => {
        setDeleteId(id);
        setOpenDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await axios.delete(`${baseUrl}/admin/nav-icons/hamburger-menu/${deleteId}`, {
                headers: { Authorization: token }
            });
            toast.success('Hamburger Menu item deleted successfully');
            fetchItems(page, pageSize);
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(error?.response?.data?.message || 'Failed to delete Hamburger Menu item');
        } finally {
            setDeleting(false);
            setOpenDeleteModal(false);
            setDeleteId(null);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        setStatusUpdating(prev => ({ ...prev, [id]: true }));
        try {
            await axios.patch(
                `${baseUrl}/admin/nav-icons/hamburger-menu/toggle/${id}`,
                {},
                { headers: { Authorization: token } }
            );
            toast.success('Status updated successfully');
            // Optimistic update
            setItems(prevItems =>
                prevItems.map(item =>
                    item._id === id ? { ...item, isActive: !currentStatus } : item
                )
            );
            fetchItems(page, pageSize);
        } catch (error) {
            console.error('Toggle status error:', error);
            toast.error(error?.response?.data?.message || 'Failed to update status');
        } finally {
            setStatusUpdating(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleToggleGuestStatus = async (id, currentStatus) => {
        setGuestStatusUpdating(prev => ({ ...prev, [id]: true }));
        try {
            const res = await axios.patch(
                `${baseUrl}/admin/nav-icons/hamburger-menu/toggleGuestStatus/${id}`,
                {},
                { headers: { Authorization: token } }
            );
            toast.success('Guest user status updated successfully');
            const updatedData = res?.data?.data || res?.data;
            if (updatedData && typeof updatedData === 'object' && updatedData._id === id) {
                setItems(prevItems =>
                    prevItems.map(item => (item._id === id ? { ...item, ...updatedData } : item))
                );
            } else {
                setItems(prevItems =>
                    prevItems.map(item => {
                        if (item._id !== id) return item;
                        const key = ('isGuest' in item) ? 'isGuest'
                            : ('isGuestUser' in item) ? 'isGuestUser'
                            : ('isForGuest' in item) ? 'isForGuest'
                            : ('isGuestActive' in item) ? 'isGuestActive'
                            : 'isGuest';
                        return { ...item, [key]: !currentStatus };
                    })
                );
            }
            fetchItems(page, pageSize);
        } catch (error) {
            console.error('Toggle guest status error:', error);
            toast.error(error?.response?.data?.message || 'Failed to update guest user status');
        } finally {
            setGuestStatusUpdating(prev => ({ ...prev, [id]: false }));
        }
    };

    const columns = [
        {
            field: 'iconUrl',
            headerName: 'Icon',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Avatar
                        src={params.value || params.row?.iconImage}
                        alt={params.row?.name || 'Icon'}
                        variant="rounded"
                        sx={{ width: 38, height: 38, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}
                        imgProps={{ style: { objectFit: 'contain' } }}
                    />
                </Box>
            )
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 160,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={600} color="#1e293b">
                    {params.value || '-'}
                </Typography>
            )
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 170,
            renderCell: (params) => {
                if (!params.value) {
                    return (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not Selected
                        </Typography>
                    );
                }
                const opt = TYPE_OPTIONS.find(o => o.value === params.value);
                const label = opt ? opt.label : params.value.replace(/_/g, ' ');
                return (
                    <Chip
                        label={label}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: '#cbd5e1',
                            color: '#334155',
                            fontWeight: 500,
                            fontSize: '12px',
                            backgroundColor: '#f8fafc'
                        }}
                    />
                );
            }
        },
        {
            field: 'badgeText',
            headerName: 'Badge',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                params.value ? (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(231, 178, 66, 0.15)',
                            color: 'var(--primary-color, #E7B242)',
                            fontWeight: 700,
                            fontSize: '11px',
                            borderRadius: '4px'
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                )
            )
        },
        {
            field: 'createdAt',
            headerName: 'Created At',
            width: 170,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {params.value ? new Date(params.value).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                    }) : '-'}
                </Typography>
            )
        },
        {
            field: 'isActive',
            headerName: 'Status',
            width: 110,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Switch
                    checked={Boolean(params.value)}
                    color="success"
                    disabled={Boolean(statusUpdating[params.row._id])}
                    onChange={() => handleToggleStatus(params.row._id, params.value)}
                />
            )
        },
        {
            field: 'isGuest',
            headerName: 'Guest User',
            width: 125,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const isChecked = Boolean(
                    params.row?.isGuest ??
                    params.row?.isGuestUser ??
                    params.row?.isForGuest ??
                    params.row?.isGuestActive ??
                    params.value
                );
                return (
                    <Switch
                        checked={isChecked}
                        color="primary"
                        disabled={Boolean(guestStatusUpdating[params.row._id])}
                        onChange={() => handleToggleGuestStatus(params.row._id, isChecked)}
                    />
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={0.5}>
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenModal(params.row)}
                            sx={{ '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } }}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {/* <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDelete(params.row._id)}
                            sx={{ '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip> */}
                </Box>
            )
        }
    ];

    return (
        <div className="container-xxl">
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                mb={3}
                mt={2}
            >
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="#1e293b">
                        Hamburger Menu
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage mobile application hamburger menu navigation items
                    </Typography>
                </Box>
                <Box display="flex" gap={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => fetchItems(page, pageSize)}
                        disabled={loading}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        Refresh
                    </Button>
                    {/* <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenModal()}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            backgroundColor: 'var(--primary-color, #E7B242)',
                            '&:hover': { backgroundColor: 'var(--primary-color, #E7B242)', opacity: 0.9 }
                        }}
                    >
                        Add Menu Item
                    </Button> */}
                </Box>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    bgcolor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    p: 2
                }}
            >
                <DataGrid
                    rows={items}
                    columns={columns}
                    getRowId={(row) => row._id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalDocs}
                    paginationModel={{ page: page - 1, pageSize }}
                    onPaginationModelChange={(newModel) => {
                        setPage(newModel.page + 1);
                        setPageSize(newModel.pageSize);
                    }}
                    pageSizeOptions={[5, 10, 20, 50]}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f8fafc',
                            fontWeight: 700,
                            borderRadius: '8px'
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center'
                        }
                    }}
                />
            </Box>

            {/* Add / Edit Dialog */}
            <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    {editId ? 'Edit Hamburger Menu Item' : 'Add Hamburger Menu Item'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
                        <TextField
                            label="Menu Item Name"
                            required
                            fullWidth
                            size="small"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g. My Session, Horoscope, Kundli"
                        />

                        {editId ? (
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: '8px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.25}>
                                        Type
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color={formData.type ? '#1e293b' : '#dc2626'}>
                                        {formData.type
                                            ? (TYPE_OPTIONS.find(o => o.value === formData.type)?.label || formData.type)
                                            : 'Not Selected'}
                                    </Typography>
                                </Box>
                                {formData.type ? (
                                    <Chip
                                        label={formData.type}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            borderColor: '#cbd5e1',
                                            color: '#475569',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            backgroundColor: '#ffffff'
                                        }}
                                    />
                                ) : (
                                    <Chip
                                        label="Not Selected"
                                        size="small"
                                        sx={{
                                            backgroundColor: '#fee2e2',
                                            color: '#dc2626',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    />
                                )}
                            </Box>
                        ) : (
                            <TextField
                                select
                                label="Type"
                                required
                                fullWidth
                                size="small"
                                value={formData.type}
                                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                                helperText="Select menu item navigation type"
                            >
                                <MenuItem value="" disabled>
                                    <em>Select Type</em>
                                </MenuItem>
                                {TYPE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label} ({opt.value})
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        <TextField
                            label="Badge Text (Optional)"
                            fullWidth
                            size="small"
                            value={formData.badgeText}
                            onChange={(e) => setFormData(prev => ({ ...prev, badgeText: e.target.value }))}
                            placeholder="e.g. NEW, HOT, FREE"
                            helperText="Leave empty if no badge is required"
                        />

                        <Box>
                            <Typography variant="subtitle2" fontWeight={600} color="#334155" mb={1}>
                                Icon Image *
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                                {formData.iconUrl ? (
                                    <Box position="relative">
                                        <img
                                            src={formData.iconUrl}
                                            alt="Icon Preview"
                                            style={{
                                                width: 70,
                                                height: 70,
                                                objectFit: 'contain',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: 8,
                                                padding: 4,
                                                backgroundColor: '#f8fafc'
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        width={70}
                                        height={70}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        bgcolor="#f8fafc"
                                        border="1px dashed #cbd5e1"
                                        borderRadius={2}
                                    >
                                        <PhotoCamera color="disabled" />
                                    </Box>
                                )}
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        size="small"
                                        disabled={imageUploading}
                                        startIcon={imageUploading ? <CircularProgress size={16} /> : <PhotoCamera />}
                                        sx={{ textTransform: 'none', borderRadius: '6px' }}
                                    >
                                        {imageUploading ? 'Uploading...' : 'Upload Image'}
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,.svg"
                                            onChange={handleImageUpload}
                                        />
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        Supports PNG, JPG, WEBP, SVG
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseModal} color="inherit" disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || imageUploading}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '6px',
                            backgroundColor: 'var(--primary-color, #E7B242)',
                            '&:hover': { backgroundColor: 'var(--primary-color, #E7B242)', opacity: 0.9 }
                        }}
                    >
                        {saving ? 'Saving...' : editId ? 'Update Item' : 'Create Item'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteModal} onClose={() => !deleting && setOpenDeleteModal(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this Hamburger Menu item? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenDeleteModal(false)} color="inherit" disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        sx={{ textTransform: 'none', borderRadius: '6px' }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default HamburgerMenu;
